/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner';

import { InstrumentedCustomThemeButton as InstrumentedButton } from '../../analytics/InstrumentedButton';
import { useAnalytics } from '../../analytics';
import { noInvitePermissionRendered } from '../../analytics/ufoExperiences';
import { getProductTitle } from '../../utils';
import { useInvitePeopleContext } from '../InvitePeople';
import { messages } from '../i18n';
import { NoInvitePermissionAnalytics } from './NoInvitePermissionAnalytics';
import LazyLoadedWarningIllustration from './LazyLoadWarningIllustration';
import useOpenInviteNote from './useOpenInviteNote';

const styles = cssMap({
	formWrapper: {
		position: 'relative',
		maxWidth: '354px',
		marginInline: 'auto',
	},
	descriptionWrapper: {
		marginBlock: token('space.200', '16px'),
	},
	buttonWrapper: {
		flexFlow: 'row-reverse',
		marginBottom: token('space.200', '16px'),
	},
	spinnerWrapper: {
		marginBlock: token('space.800', '64px'),
		marginInline: token('space.800', '64px'),
		overflowY: 'auto',
	},
	headingWrapper: {
		paddingBlockStart: token('space.300', '24px'),
		paddingBlockEnd: token('space.025', '2px'),
	},
	footer: {
		marginBottom: token('space.300', '24px'),
	},
});

type OwnProps = {
	onAcknowledgeButtonClick?: () => void;
};

const NoInvitePermission: React.FC<OwnProps> = ({ onAcknowledgeButtonClick }) => {
	const analytics = useAnalytics(NoInvitePermissionAnalytics);
	const { jiraSubProduct, orgId, productId, userRole } = useInvitePeopleContext();
	const {
		failedGetOpenInviteNote,
		isLoading: isFetchingOpenInviteNote,
		openInviteNote,
	} = useOpenInviteNote(orgId);

	useEffect(() => {
		noInvitePermissionRendered.addMetadata({
			orgId,
			userRole,
		});
		noInvitePermissionRendered.start();
	}, [orgId, userRole]);

	useEffect(() => {
		if (!isFetchingOpenInviteNote) {
			noInvitePermissionRendered.success({
				metadata: {
					orgId,
					userRole,
					failedGetOpenInviteNote,
				},
			});
		}
	}, [failedGetOpenInviteNote, isFetchingOpenInviteNote, orgId, userRole]);

	useEffect(() => {
		analytics.viewedNoInvitePermissionScreen();
	}, [analytics]);

	const Wrapper = ({ children }: { children: React.ReactNode }) => (
		<Box testId="testId-no-invite-permission">
			<Box xcss={styles.formWrapper}>
				<Inline spread="space-between">
					<Box xcss={styles.headingWrapper}>
						<Heading as="h2" id="invite-people-title" size="medium">
							<FormattedMessage {...messages.noInvitePermissionTitle} />
						</Heading>
					</Box>
				</Inline>
				{children}
			</Box>
		</Box>
	);

	if (isFetchingOpenInviteNote) {
		return (
			<Wrapper>
				<Inline
					alignBlock="center"
					alignInline="center"
					testId="testId-no-invite-permission-spinner"
					xcss={styles.spinnerWrapper}
				>
					<Spinner />
				</Inline>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<Inline alignBlock="center" xcss={styles.descriptionWrapper}>
				<LazyLoadedWarningIllustration />
				<Text as="p" testId="testId-no-invite-permission-description">
					{!!openInviteNote ? (
						openInviteNote
					) : (
						<FormattedMessage
							{...messages.defaultNoInvitePermissionDescription}
							values={{
								productName: getProductTitle(jiraSubProduct || productId),
							}}
						/>
					)}
				</Text>
			</Inline>
			<Box xcss={styles.footer}>
				<Inline space="space.100" xcss={styles.buttonWrapper}>
					<InstrumentedButton
						actionSubjectId={'closeNoInvitePermissionButton'}
						appearance="primary"
						onClick={onAcknowledgeButtonClick}
						type="button"
					>
						<FormattedMessage {...messages.noInvitePermissionButtonTitle} />
					</InstrumentedButton>
				</Inline>
			</Box>
		</Wrapper>
	);
};

export default NoInvitePermission;
